import { useRef } from 'react';
import { extractApiErrorMessage } from '@features/billing-readiness/utils/errorUtils';
import { useAutoSave } from '@hooks/useAutoSave';
import { useSnackbar } from 'notistack';

import { OrganizationManagedBillingCard } from '../components/organization-managed-billing-card';
import {
  useJotBillManagedBillingQuery,
  useUpdateJotBillManagedBillingMutation,
} from '../store/hooks';

interface OrganizationManagedBillingCardContainerProps {
  organizationId: string;
}

interface ManagedBillingChange {
  managed: boolean;
  reason?: string;
}

/**
 * Loads and saves the organization's managed-billing setting. A change saves
 * immediately; the saved value comes back through the query cache. Only the
 * current organization's result is shown (`currentData`), so switching
 * organizations never presents the previous one's choice as editable.
 * Marking managed without a signed managed agreement sends the staff reason.
 */
export function OrganizationManagedBillingCardContainer({
  organizationId,
}: OrganizationManagedBillingCardContainerProps) {
  const saving = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const { currentData, isFetching, isError, refetch } = useJotBillManagedBillingQuery({
    organizationId,
  });
  const [updateManagedBilling] = useUpdateJotBillManagedBillingMutation();

  const { status, saveImmediatelyAndWait } = useAutoSave<ManagedBillingChange>({
    onSave: async ({ managed, reason }) => {
      await updateManagedBilling({
        organizationId,
        updateJotBillManagedBillingRequest: reason ? { managed, reason } : { managed },
      }).unwrap();
    },
  });

  const changeManaged = async (managed: boolean, reason?: string): Promise<boolean> => {
    if (saving.current) return false;
    saving.current = true;
    try {
      await saveImmediatelyAndWait({ managed, reason });
      return true;
    } catch (error) {
      enqueueSnackbar(extractApiErrorMessage(error, 'Failed to save the billing service'), {
        variant: 'error',
      });
      return false;
    } finally {
      saving.current = false;
    }
  };

  return (
    <OrganizationManagedBillingCard
      managed={currentData?.managed}
      updatedAt={currentData?.updated_at}
      updatedBy={currentData?.updated_by}
      signedManagedAgreement={currentData?.signed_managed_agreement}
      isLoading={currentData === undefined && isFetching}
      loadFailed={currentData === undefined && !isFetching && isError}
      onRetry={() => void refetch()}
      saveStatus={status}
      onChange={changeManaged}
    />
  );
}
